// The case's landing effects (spec web/case-opening): one canvas over the page, each rarity its
// own celebration, explosions from Mythic up and a rain of coins for the jackpot. This module is
// the model: a scene of particles, rings and a flash, a step per frame, and each tier's plan of
// timed actions. The dialog draws it on a canvas, shakes itself and plays the sounds the plan names.
import palette from "./effects.json";
import type { Tier } from "./reel";

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  drag: number;
  life: number;
  max: number;
  size: number;
  color: string;
  kind: "spark" | "coin" | "smoke";
  spin: number;
}

export interface Ring {
  x: number;
  y: number;
  color: string;
  radius: number;
  life: number;
  max: number;
  width: number;
}

export interface Flash {
  color: string;
  alpha: number;
  life: number;
  max: number;
}

export interface Scene {
  particles: Particle[];
  rings: Ring[];
  flash: Flash | null;
}

/** At most this many particles at once, so a phone keeps its frame rate. */
export const MAX_PARTICLES = 1400;

export const emptyScene = (): Scene => ({ particles: [], rings: [], flash: null });

interface BurstOptions {
  life?: number;
  gravity?: number;
  drag?: number;
  size?: number;
  lift?: number;
  kind?: Particle["kind"];
}

export function burst(
  scene: Scene,
  x: number,
  y: number,
  colors: readonly string[],
  count: number,
  speed: number,
  options: BurstOptions,
  random: () => number,
): void {
  for (let made = 0; made < count && scene.particles.length < MAX_PARTICLES; made += 1) {
    const angle = random() * Math.PI * 2;
    const velocity = speed * (0.35 + random() * 0.9);
    scene.particles.push({
      x,
      y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity - (options.lift ?? 0),
      gravity: options.gravity ?? 0.12,
      drag: options.drag ?? 0.985,
      life: 0,
      max: (options.life ?? 70) * (0.6 + random() * 0.6),
      size: (options.size ?? 3) * (0.6 + random() * 0.8),
      color: colors[Math.floor(random() * colors.length)] ?? palette.white,
      kind: options.kind ?? "spark",
      spin: random() * 6,
    });
  }
}

export function ring(scene: Scene, x: number, y: number, color: string, radius: number, max: number, width = 4): void {
  scene.rings.push({ x, y, color, radius, life: 0, max, width });
}

/** One frame: everything moves and ages, and what has faded or fallen off is dropped. */
export function step(scene: Scene, height: number): boolean {
  for (const each of scene.rings) each.life += 1;
  for (const each of scene.particles) {
    each.life += 1;
    each.vx *= each.drag;
    each.vy = each.vy * each.drag + each.gravity;
    each.x += each.vx;
    each.y += each.vy;
    each.spin += 0.2;
  }
  if (scene.flash !== null) {
    scene.flash.life += 1;
    if (scene.flash.life >= scene.flash.max) scene.flash = null;
  }
  scene.particles = scene.particles.filter((each) => each.life < each.max && each.y < height + 40);
  scene.rings = scene.rings.filter((each) => each.life < each.max);
  return scene.particles.length > 0 || scene.rings.length > 0 || scene.flash !== null;
}

/** A timed action of a landing: what it adds to the scene, and the shake and sound it asks for. */
export interface Beat {
  at: number;
  run: (scene: Scene) => void;
  shake?: { amplitude: number; ms: number };
  boom?: "small" | "big";
  shout?: { text: string; color: string };
}

/** Where the card landed and the viewport, in CSS pixels. */
export interface Stage {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Each tier's celebration as timed beats; a larger tier is louder. Reduced motion has none. */
export function landing(tier: Tier, color: string, stage: Stage, random: () => number): Beat[] {
  const { x, y } = stage;
  switch (tier) {
    case "common":
      return [
        {
          at: 0,
          run: (s) => {
            ring(s, x, y, color, 90, 40, 3);
            burst(s, x, y, [color, palette.white], 16, 4, { life: 40, gravity: 0.05 }, random);
          },
        },
      ];
    case "rare":
      return [
        {
          at: 0,
          run: (s) => {
            ring(s, x, y, color, 130, 46, 4);
            burst(s, x, y, [color, palette.rareSpark, palette.white], 36, 6, { life: 55 }, random);
          },
        },
      ];
    case "epic":
      return [
        {
          at: 0,
          run: (s) => {
            ring(s, x, y, color, 170, 52, 5);
            burst(s, x, y, [color, palette.epicSpark, palette.white], 70, 8, { life: 65 }, random);
          },
          shake: { amplitude: 3, ms: 260 },
        },
        { at: 120, run: (s) => ring(s, x, y, palette.white, 120, 40, 3) },
      ];
    case "legendary":
      return [
        {
          at: 0,
          run: (s) => {
            s.flash = { color, alpha: 0.18, life: 0, max: 18 };
            ring(s, x, y, color, 230, 60, 6);
            burst(s, x, y, [color, ...palette.legendarySparks, palette.white], 110, 10, { life: 75 }, random);
          },
          shake: { amplitude: 5, ms: 360 },
          shout: { text: "LEGENDARY", color },
        },
        {
          at: 140,
          run: (s) => {
            ring(s, x, y, palette.legendaryRing, 170, 50, 4);
            burst(s, x, y, [palette.white, color], 50, 9, { life: 60 }, random);
          },
        },
      ];
    case "mythic": {
      // Explosions: five blasts around the card, each a shockwave, sparks and smoke, then a hard shake.
      const reach = stage.width < 700 ? 0.5 : 1;
      const blasts: readonly (readonly [number, number])[] = [[0, 0], [-170, -50], [170, -40], [-110, 60], [120, 70]];
      return blasts.map(([dx, dy], at) => ({
        at: at * 150,
        run: (s: Scene) => {
          const bx = x + dx * reach;
          const by = y + dy;
          s.flash = at === 0 ? { color: palette.white, alpha: 0.55, life: 0, max: 14 } : { color: palette.mythicFlash, alpha: 0.22, life: 0, max: 10 };
          ring(s, bx, by, palette.mythicRing, 160, 34, 7);
          ring(s, bx, by, color, 240, 52, 4);
          burst(s, bx, by, [...palette.mythicFire, color], 120, 13, { life: 70, gravity: 0.16 }, random);
          burst(s, bx, by, palette.smoke, 10, 2.2, { kind: "smoke", life: 70, gravity: -0.02, size: 10, drag: 0.96 }, random);
        },
        shake: { amplitude: 9 - at, ms: 380 },
        boom: "big",
        ...(at === 0 ? { shout: { text: "MYTHIC", color } } : {}),
      }));
    }
    case "jackpot": {
      // Fireworks across the screen, a rain of coins and a gold flash.
      const fireworks: Beat[] = Array.from({ length: 9 }, (_, at) => ({
        at: at * 220,
        run: (s: Scene) => {
          const fx = stage.width * (0.12 + random() * 0.76);
          const fy = stage.height * (0.14 + random() * 0.36);
          const hue = palette.fireworks[at % palette.fireworks.length] ?? palette.gold;
          ring(s, fx, fy, hue, 150, 40, 3);
          burst(s, fx, fy, [hue, palette.white], 110, 9, { life: 80, gravity: 0.09 }, random);
        },
        boom: "small",
      }));
      const coins: Beat[] = Array.from({ length: 14 }, (_, at) => ({
        at: at * 120,
        run: (s: Scene) => {
          for (let made = 0; made < 10 && s.particles.length < MAX_PARTICLES; made += 1) {
            s.particles.push({
              x: random() * stage.width,
              y: -20 - random() * 60,
              vx: (random() - 0.5) * 1.5,
              vy: 2 + random() * 3,
              gravity: 0.1,
              drag: 0.995,
              life: 0,
              max: 220,
              size: 7 + random() * 4,
              color: palette.gold,
              kind: "coin",
              spin: random() * 6,
            });
          }
        },
      }));
      const opening: Beat = {
        at: 0,
        run: (s) => {
          s.flash = { color: palette.gold, alpha: 0.4, life: 0, max: 22 };
        },
        shake: { amplitude: 7, ms: 520 },
        shout: { text: "JACKPOT", color: palette.gold },
      };
      return [opening, ...fireworks, ...coins];
    }
  }
}
